// A card per sales channel for the selected period. The top source is
// DERIVED here from locations × mix at render time — never stored — so it
// can never drift out of agreement with the mix bars in the strip above.
import { SnapshotChannel, SnapshotLocation, SnapshotPeriod } from "./types";
import {
  CHANNEL_RAMP,
  DeltaChip,
  EM_DASH,
  OTHER_CHANNEL_LABEL,
  un,
  usd,
  useSnapshotTokens,
} from "./primitives";
import { useGuardedNavigation } from "../../hooks/use_guarded_navigation";

/** Largest contributing location for a channel, from the same mix data the
 *  strip renders. Returns null when no location reports that channel. */
const topSourceFor = (
  channel: string,
  locations: SnapshotLocation[]
): { name: string; share: number } | null => {
  let bestName: string | null = null;
  let bestUnits = 0;
  let total = 0;
  for (const l of locations) {
    const entry = l.channelMix?.find((m) => m.channel === channel);
    if (!entry) {
      continue;
    }
    total += entry.units;
    if (entry.units > bestUnits) {
      bestUnits = entry.units;
      bestName = l.name;
    }
  }
  if (!bestName || total <= 0) {
    return null;
  }
  return { name: bestName, share: bestUnits / total };
};

export const ChannelCards = ({
  channels,
  locations,
  period,
  demandPath,
}: {
  channels: SnapshotChannel[];
  locations: SnapshotLocation[];
  period: SnapshotPeriod;
  demandPath: string;
}) => {
  const t = useSnapshotTokens();
  const { navigateWithGuards } = useGuardedNavigation();
  if (!channels.length) {
    return (
      <span
        title="No channel-level sales rows for this run"
        style={{ fontSize: 12, color: t.ink4 }}
      >
        No channel breakdown available for this run
      </span>
    );
  }
  const per = period === "month" ? "/mo" : "/wk";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${channels.length}, minmax(0, 1fr))`,
        gap: 12,
      }}
    >
      {channels.map((c, i) => {
        const color = c.isOther
          ? t.ink4
          : CHANNEL_RAMP[i % CHANNEL_RAMP.length];
        const source = c.isOther ? null : topSourceFor(c.channel, locations);
        return (
          <div
            key={c.channel}
            role="link"
            tabIndex={0}
            title={`Open Demand · ${c.channel}`}
            onClick={() => navigateWithGuards(demandPath)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                navigateWithGuards(demandPath);
              }
            }}
            style={{
              position: "relative",
              padding: "11px 13px",
              background: t.surface,
              border: `1px solid ${t.line}`,
              borderRadius: 8,
              cursor: "pointer",
              minWidth: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = t.ink5;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = t.line;
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              style={{ position: "absolute", top: 10, right: 10, opacity: 0.4 }}
            >
              <path
                d="M2.5 7.5L7.5 2.5M4 2.5h3.5V6"
                stroke="currentColor"
                strokeWidth="1.3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 5,
                paddingRight: 14,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: color,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: c.isOther ? t.ink3 : t.ink2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.channel}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
              <span
                style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.4 }}
              >
                {un(c.units)}
                <span
                  style={{ fontSize: 11, fontWeight: 500, color: t.ink3 }}
                >
                  {" u"}
                  {per}
                </span>
              </span>
              <DeltaChip current={null} prev={null} unit="u" />
            </div>
            <div
              style={{
                position: "relative",
                height: 6,
                background: t.line2,
                borderRadius: 3,
                overflow: "hidden",
                margin: "7px 0 5px",
              }}
              title={`${(c.share * 100).toFixed(1)}% of outflow`}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  width: c.share * 100 + "%",
                  background: color,
                  opacity: 0.75,
                  borderRadius: 3,
                }}
              />
            </div>
            <div style={{ fontSize: 10.5, color: t.ink4 }}>
              {(c.share * 100).toFixed(1)}% of outflow
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: t.ink2,
                fontWeight: 600,
                marginTop: 5,
              }}
            >
              {c.revenue == null ? EM_DASH : usd(c.revenue, 2)}
              <span style={{ fontWeight: 400, color: t.ink4 }}>
                {" revenue"}
                {per}
              </span>
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: t.ink4,
                marginTop: 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={
                c.isOther
                  ? `Every channel outside the top ${CHANNEL_RAMP.length}`
                  : undefined
              }
            >
              {c.isOther
                ? "Rolled-up remainder"
                : source
                ? `Mostly ${source.name} · ${Math.round(source.share * 100)}%`
                : EM_DASH}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { OTHER_CHANNEL_LABEL };
